export const APP_NAME = 'Showcase by ReHub';
export const APP_TAGLINE = 'Securely elevate any listing with a single link';

export const SHOWCASE_VIDEO = {
  title: 'Showcase by ReHub',
  embedUrl: 'https://www.youtube.com/embed/DYsB6xK-42s',
};

export const SHOWCASE_BASE_URL =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SHOWCASE_BASE_URL) ||
  'https://showcase.rehub.studio';

export const SECTION_TYPES = [
  'hero',
  'gallery',
  'imagemotion_video',
  'standard_video',
  'tour_360',
  'planos_floorplan',
  'floorplan_pdf',
  'description',
  'key_features',
  'epc',
  'map_location',
  'documents',
  'brochure_download',
  'contact',
  'ai_assistant',
] as const;

export type SectionType = (typeof SECTION_TYPES)[number];

export const SECTION_LABELS: Record<SectionType, string> = {
  hero: 'Hero Image',
  gallery: 'Photo Gallery',
  imagemotion_video: 'ImageMotion Video',
  standard_video: 'Standard Video',
  tour_360: '360 Tour',
  planos_floorplan: 'Interactive Floor Plan',
  floorplan_pdf: 'Floor Plan PDF',
  description: 'Property Description',
  key_features: 'Key Features',
  epc: 'EPC Rating',
  map_location: 'Map & Location',
  documents: 'Download Documents',
  brochure_download: 'Brochure Download',
  contact: 'Contact Section',
  ai_assistant: 'AI Property Assistant',
};

export const SECTION_ICONS: Record<SectionType, string> = {
  hero: 'Image',
  gallery: 'Images',
  imagemotion_video: 'Clapperboard',
  standard_video: 'Video',
  tour_360: 'View',
  planos_floorplan: 'LayoutDashboard',
  floorplan_pdf: 'FileImage',
  description: 'FileText',
  key_features: 'Star',
  epc: 'Zap',
  map_location: 'MapPin',
  documents: 'Download',
  brochure_download: 'BookOpen',
  contact: 'Phone',
  ai_assistant: 'MessageCircle',
};

export const SMART_PROMPTS: Partial<Record<SectionType, { message: string; appLink: string; appName: string }>> = {
  imagemotion_video: {
    message: 'No video attached. Generate one in ImageMotion, or paste a URL below.',
    appLink: 'https://imagemotion.rehub.studio',
    appName: 'ImageMotion',
  },
  tour_360: {
    message: 'No 360 tour attached. Add one from Active Spaces, or paste an embed URL below.',
    appLink: 'https://tours.rehub.studio',
    appName: '360 Tours',
  },
  planos_floorplan: {
    message: 'No floor plan attached. Create one in Planos, or paste an embed URL below.',
    appLink: 'https://planos.rehub.studio',
    appName: 'Planos',
  },
  standard_video: {
    message: 'No video attached. Upload one in Resources, paste a YouTube/Vimeo link below, or create one in VideoMotion.',
    appLink: 'https://videomotion.rehub.studio',
    appName: 'VideoMotion',
  },
};

export const RESOURCE_TYPES = [
  'hero_image',
  'gallery',
  'image',
  'standard_video',
  'imagemotion_video',
  'tour_360',
  'planos_embed',
  'floorplan_pdf',
  'document_pdf',
  'brochure_pdf',
  'map',
  'contact_block',
  'text_block',
  'qr_code',
  'ai_assistant',
] as const;

export type ResourceType = (typeof RESOURCE_TYPES)[number];

export const ORIGIN_APPS = ['imagemotion', 'videomotion', 'planos', 'tours', 'photoclear', 'upload', 'photoplan'] as const;
export type OriginApp = (typeof ORIGIN_APPS)[number];

export const SHOWCASE_STATUSES = ['draft', 'published', 'archived'] as const;
export type ShowcaseStatus = (typeof SHOWCASE_STATUSES)[number];

export const PROPERTY_TYPES = [
  'Detached',
  'Semi-Detached',
  'Terraced',
  'Flat / Apartment',
  'Bungalow',
  'Cottage',
  'Penthouse',
  'Townhouse',
  'Villa',
  'New Build',
  'Commercial',
  'Land',
  'Other',
] as const;

export const TENURE_TYPES = ['Freehold', 'Leasehold', 'Share of Freehold', 'Commonhold', 'Other'] as const;

export const PROPERTY_STATUSES = [
  'Available',
  'New Instruction',
  'Price Reduced',
  'Under Offer',
  'Sold STC',
  'Sold',
  'Let Agreed',
  'Coming Soon',
] as const;

export const SECTION_RESOURCE_TYPE_MAP: Partial<Record<SectionType, ResourceType[]>> = {
  hero: ['hero_image'],
  gallery: ['gallery', 'image'],
  imagemotion_video: ['imagemotion_video'],
  standard_video: ['standard_video'],
  tour_360: ['tour_360'],
  planos_floorplan: ['planos_embed'],
  floorplan_pdf: ['floorplan_pdf'],
  documents: ['document_pdf', 'brochure_pdf'],
  brochure_download: ['brochure_pdf'],
  contact: ['contact_block'],
};

export function getResourcesForSection(
  section: SectionType,
  resources: { type: string }[]
): typeof resources {
  const types = SECTION_RESOURCE_TYPE_MAP[section];
  if (!types) return [];
  return resources.filter((r) => (types as string[]).includes(r.type));
}

export function sectionHasResource(
  section: SectionType,
  resources: { type: string }[]
): boolean {
  const types = SECTION_RESOURCE_TYPE_MAP[section];
  if (!types) return true;
  return resources.some((r) => (types as string[]).includes(r.type));
}
